import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import CtaSection from "../components/CtaSection";
import { Reveal } from "../components/Reveal";
import { IMAGES, COMPANY } from "../data/site";
import { useSiteImages } from "../lib/useSiteImages";

const API = process.env.REACT_APP_BACKEND_URL;

const POSITIONS = [
  { title: "現場作業員（土木・造成）", type: "正社員", desc: "造成・整地・外構など土木現場での作業。未経験からでも、先輩が一から教えます。", tags: ["未経験OK", "資格取得支援"], detail: "造成・整地・外構など土木現場での作業全般を担当します。未経験でも先輩スタッフが一から指導。重機や測量など、興味に応じてスキルアップできます。" },
  { title: "重機オペレーター", type: "正社員", desc: "バックホウ等の重機オペレーション。車両系建設機械の資格をお持ちの方。", tags: ["資格必須", "経験者優遇"], detail: "バックホウなどの重機オペレーションを担当。造成現場の要となるポジションです。車両系建設機械の資格をお持ちの方、経験者は優遇します。" },
  { title: "施工管理", type: "正社員", desc: "土木現場の工程・品質・安全管理。施工管理技士の有資格者、または取得を目指す方。", tags: ["キャリア採用", "資格手当"], detail: "工程・品質・安全・原価の管理を担います。施工管理技士の有資格者は即戦力として、未取得の方は資格取得支援制度でキャリアアップできます。" },
  { title: "不動産管理スタッフ", type: "正社員 / パート", desc: "管理物件の巡回・入居者対応・事務。建設と不動産の両方に関われる仕事です。", tags: ["未経験OK", "土日祝休み"], detail: "管理物件の巡回、入居者対応、事務業務が中心です。建設と不動産の両事業に関われる珍しいポジション。パート勤務もご相談いただけます。" },
];

function PositionCards({ positions }) {
  const [active, setActive] = useState(null);

  return (
    <>
      <div className="mt-16 grid gap-6 md:grid-cols-2">
        {positions.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.06}>
            <button
              type="button"
              data-testid={`position-card-${i}`}
              onClick={() => setActive({ ...p, num: String(i + 1).padStart(2, "0") })}
              className="group h-full w-full cursor-pointer border border-line bg-panel p-8 text-left transition-colors hover:border-accent"
            >
              <div className="flex items-center justify-between gap-4">
                <span className="bg-accent px-3 py-1 font-mono text-[10px] font-bold tracking-[0.2em] text-ink">{p.type}</span>
                <span className="font-display text-3xl font-extrabold text-steel transition-colors group-hover:text-accent">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="mt-5 font-mincho text-xl font-bold text-white">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-gray-400">{p.desc}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {p.tags.map((t) => (
                  <span key={t} className="border border-line px-3 py-1 text-[11px] text-gray-400">
                    {t}
                  </span>
                ))}
              </div>
              <p className="mt-5 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-accent opacity-0 transition-opacity group-hover:opacity-100">
                詳しく見る
                <ArrowUpRight className="h-3.5 w-3.5" />
              </p>
            </button>
          </Reveal>
        ))}
      </div>
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm"
            onClick={() => setActive(null)}
          >
            <motion.div
              data-testid="position-modal"
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="clip-corner relative w-full max-w-lg border border-line bg-coal p-10"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                data-testid="position-modal-close"
                onClick={() => setActive(null)}
                className="absolute right-5 top-5 text-gray-500 transition-colors hover:text-accent"
                aria-label="閉じる"
              >
                <X className="h-6 w-6" />
              </button>
              <div className="flex items-center gap-3">
                <span className="bg-accent px-3 py-1 font-mono text-[10px] font-bold tracking-[0.2em] text-ink">{active.type}</span>
                <span className="font-display text-3xl font-extrabold text-steel">{active.num}</span>
              </div>
              <h3 className="mt-4 font-mincho text-2xl font-bold text-white">{active.title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-gray-400">{active.desc}</p>
              <p className="mt-6 border-t border-line pt-6 text-sm leading-loose text-gray-300">{active.detail}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {active.tags.map((t) => (
                  <span key={t} className="border border-line px-3 py-1 text-[11px] text-gray-400">
                    {t}
                  </span>
                ))}
              </div>
              <Link
                to="/contact?type=recruit"
                data-testid="position-apply-link"
                className="group mt-8 flex items-center justify-center gap-2 bg-accent px-6 py-3.5 font-display text-sm font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover"
              >
                この職種に応募する
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const REQUIREMENTS = [
  ["給与", "月給 220,000円 〜 350,000円（経験・資格による）"],
  ["勤務時間", "8:00 – 17:00（現場により変動あり）"],
  ["休日", "日曜・祝日・GW・夏季・年末年始"],
  ["勤務地", `${COMPANY.address} ほか三重県内各現場`],
  ["福利厚生", "社会保険完備・資格取得支援（全額補助）・作業服支給・車通勤可"],
];

export default function Recruit() {
  const siteImages = useSiteImages();
  const [positions, setPositions] = useState(POSITIONS);
  const [requirements, setRequirements] = useState(REQUIREMENTS);

  useEffect(() => {
    fetch(`${API}/api/recruit`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("fetch failed"))))
      .then((data) => {
        if (Array.isArray(data.positions) && data.positions.length) setPositions(data.positions);
        if (Array.isArray(data.requirements) && data.requirements.length) {
          setRequirements(data.requirements.map((r) => [r.label, r.value]));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="recruit-page">
      <PageHero
        eyebrow="JOIN OUR TEAM"
        titleEn="RECRUIT"
        titleJa="採用情報"
        desc="未来をつくる現場で、君の力を。未経験からプロフェッショナルまで、ネオ建設は挑戦する人を育てます。"
        image={siteImages.recruit || IMAGES.sunset}
      />

      <section className="border-t border-line py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="POSITIONS" title="募集職種" sub={`${positions.length}つの職種で採用中です。カードをクリックすると詳細が開きます。`} />
          <PositionCards positions={positions} />
        </div>
      </section>

      <section className="border-t border-line bg-coal py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="REQUIREMENTS" title="募集要項" />
          <Reveal delay={0.1}>
            <dl data-testid="recruit-requirements-table" className="mt-14 max-w-3xl border-t border-line">
              {requirements.map(([k, v], i) => (
                <div key={i} className="grid grid-cols-1 gap-1 border-b border-line py-5 sm:grid-cols-12 sm:gap-8">
                  <dt className="font-mono text-xs tracking-[0.2em] text-gray-500 sm:col-span-3 sm:pt-1">{k}</dt>
                  <dd className="text-sm text-gray-200 sm:col-span-9">{v}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
          <p className="mt-6 font-mono text-[10px] tracking-[0.2em] text-gray-600">※ 給与・条件は仮の情報です。正式な募集条件は確定次第更新されます。</p>
          <Reveal delay={0.15}>
            <Link
              to="/contact?type=recruit"
              data-testid="recruit-apply-cta"
              className="group mt-12 inline-flex items-center gap-3 bg-accent px-8 py-4 font-display text-sm font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover"
            >
              採用に応募・問い合わせる
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaSection title="まずは現場を見に来ませんか。" highlight="見学だけでも歓迎です。" text="採用に関するご質問・現場見学のご希望は、お問い合わせフォームからどうぞ。" button="採用の問い合わせ" to="/contact?type=recruit" />
    </div>
  );
}
